import React, { useState, useRef } from 'react';
import { User, Mail, Shield, Key, Sun, Moon, MessageSquare, LogOut, Camera, Gift, Copy, Check } from 'lucide-react';
import { useAppContext } from '../context/AppContext';

export default function ProfilePage({ 
  user, 
  setUser,
  theme = 'light', 
  toggleTheme, 
  handleLogout, 
  aiTone = 'professional', 
  setAiTone 
}) {
  const isDark = theme === 'dark';
  const name = user?.name || user?.email?.split('@')[0] || 'User';
  const email = user?.email || 'No email provided';
  const picture = user?.picture;
  
  const [avatarHovered, setAvatarHovered] = useState(false);
  const [copied, setCopied] = useState(false);
  const fileInputRef = useRef(null);

  const [windowWidth, setWindowWidth] = useState(typeof window !== 'undefined' ? window.innerWidth : 1200);

  React.useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const isDesktop = windowWidth >= 1024;

  const { referralCode, referredByCode, claimReferralCode } = useAppContext();

  const [claimInput, setClaimInput] = useState('');
  const [claimLoading, setClaimLoading] = useState(false);
  const [claimError, setClaimError] = useState(null);
  const [claimSuccess, setClaimSuccess] = useState(null);

  const myReferralCode = referralCode || name.toLowerCase().replace(/[^a-z0-9]+/g, '-') || 'user';

  const handleCopyCode = () => {
    navigator.clipboard.writeText(myReferralCode);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleApplyReferral = async (e) => {
    e.preventDefault();
    const cleanCode = claimInput.trim();
    if (!cleanCode) return;
    setClaimLoading(true);
    setClaimError(null);
    setClaimSuccess(null);

    const res = await claimReferralCode(cleanCode);
    setClaimLoading(false);
    if (res.success) {
      setClaimSuccess(res.message);
      setClaimInput('');
    } else {
      setClaimError(res.error);
    }
  };

  // Detect sign-in method
  const provider = user?.picture && user.picture.startsWith('http') ? 'Google OAuth' : 'Email & Password';

  const toneOptions = [
    { id: 'professional', label: 'Professional', desc: 'Polished, structured, & business-oriented' },
    { id: 'confident', label: 'Confident', desc: 'Bold, direct, & impact-focused' },
    { id: 'empathetic', label: 'Empathetic', desc: 'Encouraging, supportive, & warm' },
    { id: 'casual', label: 'Casual', desc: 'Friendly, relaxed, & conversational' }
  ];

  // Helper to validate picture URL (Google URLs, data URIs, etc.)
  const hasValidPicture = picture && (
    picture.startsWith('http://') || 
    picture.startsWith('https://') || 
    picture.startsWith('data:image/')
  );

  const handleAvatarClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > 2 * 1024 * 1024) {
      alert("Image must be less than 2MB.");
      return;
    }

    const reader = new FileReader();
    reader.onload = (event) => {
      const base64Data = event.target?.result;
      if (base64Data) {
        // Save to localStorage so useAuth restores it on reload
        try {
          localStorage.setItem('user_picture', base64Data);
          if (setUser) {
            setUser(prev => ({
              ...prev,
              picture: base64Data
            }));
          }
        } catch (err) {
          console.error("Failed to save avatar to localStorage:", err);
          alert("Failed to save image. File size might be too large.");
        }
      }
    };
    reader.readAsDataURL(file);
  };

  return (
    <div style={{
      flex: 1,
      width: '100%',
      height: '100%',
      overflowY: 'auto',
      background: isDark ? '#0B1220' : '#FAF9FC',
      color: isDark ? '#FAFAF8' : '#18181b',
      boxSizing: 'border-box',
      fontFamily: "'Inter', sans-serif"
    }}>
      <div style={{
        maxWidth: isDesktop ? '1100px' : '580px',
        margin: '0 auto',
        padding: '40px 24px 80px',
        display: 'flex',
        flexDirection: 'column',
        gap: '24px',
        transition: 'max-width 0.2s ease'
      }}>
        {/* Header */}
        <div>
          <h1 style={{
            fontSize: '22px',
            fontWeight: 800,
            margin: 0,
            color: isDark ? '#ffffff' : '#0f172a',
            fontFamily: "'Outfit', sans-serif"
          }}>
            Account Profile
          </h1>
          <p style={{
            fontSize: '13px',
            color: isDark ? '#a1a1aa' : '#64748b',
            marginTop: '4px',
            fontWeight: 500
          }}>
            Manage your profile, theme, and AI assistant settings.
          </p>
        </div>

        {/* Outer Columns Wrapper */}
        <div style={{
          display: isDesktop ? 'grid' : 'flex',
          gridTemplateColumns: isDesktop ? '1fr 1fr' : 'none',
          flexDirection: isDesktop ? 'row' : 'column',
          gap: '24px',
          alignItems: 'start',
          width: '100%'
        }}>
          
          {/* Left Column (Details + Logout) */}
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '24px',
            width: '100%'
          }}>
            {/* Profile Details Card */}
            <div style={{
              background: isDark ? '#14213D' : '#FFFFFF',
              border: `1px solid ${isDark ? '#1E2B4A' : '#E5E7EB'}`,
              borderRadius: '16px',
              padding: '28px',
              display: 'flex',
              flexDirection: 'column',
              gap: '24px',
              boxShadow: '0 2px 4px rgba(0,0,0,0.02)'
            }}>
              {/* Avatar Area */}
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '20px',
                borderBottom: `1px solid ${isDark ? '#1E2B4A' : '#F3F4F6'}`,
                paddingBottom: '20px'
              }}>
                {/* Clickable Avatar Wrapper */}
                <div 
                  onClick={handleAvatarClick}
                  onMouseEnter={() => setAvatarHovered(true)}
                  onMouseLeave={() => setAvatarHovered(false)}
                  style={{
                    position: 'relative',
                    width: '72px',
                    height: '72px',
                    borderRadius: '50%',
                    cursor: 'pointer',
                    overflow: 'hidden',
                    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
                    transition: 'transform 0.2s ease',
                    transform: avatarHovered ? 'scale(1.03)' : 'scale(1)',
                    background: isDark ? '#1F1F24' : '#f1f5f9'
                  }}
                >
                  {hasValidPicture ? (
                    <img 
                      src={picture} 
                      alt="Profile" 
                      style={{ 
                        width: '100%', 
                        height: '100%', 
                        objectFit: 'cover',
                        border: '2px solid var(--brand)',
                        borderRadius: '50%',
                        boxSizing: 'border-box'
                      }} 
                    />
                  ) : (
                    <div style={{
                      width: '100%',
                      height: '100%',
                      borderRadius: '50%',
                      background: 'var(--brand)',
                      color: '#FFFFFF',
                      fontSize: '26px',
                      fontWeight: 800,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      boxSizing: 'border-box',
                      border: '2px solid var(--brand)'
                    }}>
                      {name[0].toUpperCase()}
                    </div>
                  )}

                  {/* Hover overlay */}
                  <div style={{
                    position: 'absolute',
                    inset: 0,
                    background: 'rgba(0, 0, 0, 0.55)',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '2px',
                    opacity: avatarHovered ? 1 : 0,
                    transition: 'opacity 0.2s ease',
                    color: '#FFFFFF'
                  }}>
                    <Camera size={16} />
                    <span style={{ fontSize: '9px', fontWeight: 700 }}>UPLOAD</span>
                  </div>
                </div>

                {/* Hidden File Input */}
                <input 
                  type="file"
                  ref={fileInputRef}
                  onChange={handleFileChange}
                  accept="image/*"
                  style={{ display: 'none' }}
                />

                <div style={{ display: 'flex', flexDirection: 'column', gap: '3px' }}>
                  <h2 style={{
                    fontSize: '16px',
                    fontWeight: 800,
                    margin: 0,
                    color: isDark ? '#ffffff' : '#0f172a'
                  }}>
                    {name}
                  </h2>
                  <span style={{
                    fontSize: '11px',
                    color: 'var(--brand)',
                    fontWeight: 700,
                    letterSpacing: '0.3px',
                    textTransform: 'uppercase'
                  }}>
                    Resumebar User
                  </span>
                </div>
              </div>

              {/* User Fields Grid */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                {/* Field: Full Name */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                  <label style={fieldLabelStyle(isDark)}>
                    <User size={13} style={{ marginRight: '6px', verticalAlign: 'middle', display: 'inline' }} />
                    <span>Full Name</span>
                  </label>
                  <input
                    type="text"
                    readOnly
                    value={name}
                    style={fieldInputStyle(isDark)}
                  />
                </div>

                {/* Field: Email */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                  <label style={fieldLabelStyle(isDark)}>
                    <Mail size={13} style={{ marginRight: '6px', verticalAlign: 'middle', display: 'inline' }} />
                    <span>Email Address</span>
                  </label>
                  <input
                    type="text"
                    readOnly
                    value={email}
                    style={fieldInputStyle(isDark)}
                  />
                </div>

                {/* Field: Login Provider */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                  <label style={fieldLabelStyle(isDark)}>
                    <Key size={13} style={{ marginRight: '6px', verticalAlign: 'middle', display: 'inline' }} />
                    <span>Authentication Method</span>
                  </label>
                  <input
                    type="text"
                    readOnly
                    value={provider}
                    style={fieldInputStyle(isDark)}
                  />
                </div>

                {/* Field: Plan Status */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                  <label style={fieldLabelStyle(isDark)}>
                    <Shield size={13} style={{ marginRight: '6px', verticalAlign: 'middle', display: 'inline' }} />
                    <span>Account Tier</span>
                  </label>
                  <input
                    type="text"
                    readOnly
                    value="Pro Access (Active)"
                    style={{ ...fieldInputStyle(isDark), color: '#10B981', fontWeight: 600 }}
                  />
                </div>
              </div>
            </div>

            {/* Logout Action Button */}
            <button
              onClick={() => handleLogout?.()}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px',
                width: '100%',
                height: '42px',
                borderRadius: '12px',
                border: `1px solid ${isDark ? '#ef4444' : '#f87171'}`,
                background: 'transparent',
                color: '#ef4444',
                fontWeight: 700,
                fontSize: '13px',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                outline: 'none'
              }}
              onMouseEnter={e => {
                e.currentTarget.style.background = 'rgba(239, 68, 68, 0.08)';
                e.currentTarget.style.boxShadow = '0 2px 8px rgba(239, 68, 68, 0.15)';
              }}
              onMouseLeave={e => {
                e.currentTarget.style.background = 'transparent';
                e.currentTarget.style.boxShadow = 'none';
              }}
            >
              <LogOut size={16} />
              <span>Sign Out / Logout Account</span>
            </button>
          </div>

          {/* Right Column (Preferences + Referrals) */}
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '24px',
            width: '100%'
          }}>
            {/* App Customization Card */}
            <div style={{
              background: isDark ? '#14213D' : '#FFFFFF',
              border: `1px solid ${isDark ? '#1E2B4A' : '#E5E7EB'}`,
              borderRadius: '16px',
              padding: '28px',
              display: 'flex',
              flexDirection: 'column',
              gap: '24px',
              boxShadow: '0 2px 4px rgba(0,0,0,0.02)'
            }}>
              <h2 style={{
                fontSize: '15px',
                fontWeight: 800,
                margin: 0,
                color: isDark ? '#ffffff' : '#0f172a',
                fontFamily: "'Outfit', sans-serif",
                borderBottom: `1px solid ${isDark ? '#1E2B4A' : '#F3F4F6'}`,
                paddingBottom: '12px'
              }}>
                Customization & Preferences
              </h2>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                {/* Preference: Theme Switcher */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  <label style={fieldLabelStyle(isDark)}>
                    {isDark ? (
                      <Moon size={13} style={{ marginRight: '6px', verticalAlign: 'middle', display: 'inline' }} />
                    ) : (
                      <Sun size={13} style={{ marginRight: '6px', verticalAlign: 'middle', display: 'inline' }} />
                    )}
                    <span>Interface Theme</span>
                  </label>
                  <div style={{ 
                    display: 'flex', 
                    background: isDark ? '#1F1F24' : '#F3F4F6', 
                    borderRadius: '10px', 
                    padding: '4px', 
                    width: 'fit-content' 
                  }}>
                    <button 
                      onClick={() => { if (isDark) toggleTheme?.(); }}
                      style={{
                        padding: '8px 20px', 
                        borderRadius: '8px', 
                        border: 'none', 
                        fontSize: '12px', 
                        fontWeight: 700, 
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px',
                        background: !isDark ? '#FFFFFF' : 'transparent',
                        color: !isDark ? 'var(--brand)' : '#a1a1aa',
                        transition: 'all 0.2s ease',
                        boxShadow: !isDark ? '0 1px 3px rgba(0,0,0,0.08)' : 'none'
                      }}
                    >
                      <Sun size={14} />
                      <span>Light Mode</span>
                    </button>
                    <button 
                      onClick={() => { if (!isDark) toggleTheme?.(); }}
                      style={{
                        padding: '8px 20px', 
                        borderRadius: '8px', 
                        border: 'none', 
                        fontSize: '12px', 
                        fontWeight: 700, 
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px',
                        background: isDark ? '#27272a' : 'transparent',
                        color: isDark ? 'var(--brand)' : '#4b5563',
                        transition: 'all 0.2s ease',
                        boxShadow: isDark ? '0 1px 3px rgba(0,0,0,0.2)' : 'none'
                      }}
                    >
                      <Moon size={14} />
                      <span>Dark Mode</span>
                    </button>
                  </div>
                </div>

                {/* Preference: AI Tone selector */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  <label style={fieldLabelStyle(isDark)}>
                    <MessageSquare size={13} style={{ marginRight: '6px', verticalAlign: 'middle', display: 'inline' }} />
                    <span>AI Coach Writing Tone</span>
                  </label>
                  
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    <select
                      value={aiTone}
                      onChange={(e) => setAiTone?.(e.target.value)}
                      style={{
                        ...fieldInputStyle(isDark),
                        cursor: 'pointer',
                        padding: '10px 14px',
                        appearance: 'auto'
                      }}
                    >
                      {toneOptions.map(opt => (
                        <option key={opt.id} value={opt.id}>
                          {opt.label} ({opt.desc})
                        </option>
                      ))}
                    </select>
                    
                    <p style={{
                      fontSize: '11px',
                      color: isDark ? '#71717a' : '#8e9cae',
                      margin: '4px 0 0',
                      lineHeight: '1.4'
                    }}>
                      Adjusts how the AI career coach styles recommendations, resume suggestions, and interview simulations.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Refer & Earn Card */}
            <div style={{
              background: isDark ? '#14213D' : '#FFFFFF',
              border: `1px solid ${isDark ? '#1E2B4A' : '#E5E7EB'}`,
              borderRadius: '16px',
              padding: '28px',
              display: 'flex',
              flexDirection: 'column',
              gap: '20px',
              boxShadow: '0 2px 4px rgba(0,0,0,0.02)'
            }}>
              <h2 style={{
                fontSize: '15px',
                fontWeight: 800,
                margin: 0,
                color: isDark ? '#ffffff' : '#0f172a',
                fontFamily: "'Outfit', sans-serif",
                borderBottom: `1px solid ${isDark ? '#1E2B4A' : '#F3F4F6'}`,
                paddingBottom: '12px',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}>
                <Gift size={16} style={{ color: 'var(--brand)' }} />
                <span>Refer Friends & Get Credits</span>
              </h2>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
                <p style={{
                  fontSize: '12px',
                  color: isDark ? '#a1a1aa' : '#4b5563',
                  lineHeight: '1.5',
                  margin: 0
                }}>
                  Share Resumebar with friends! Both you and your friend will receive <strong style={{ color: 'var(--brand)' }}>+5 AI coaching credits</strong> when they apply your code.
                </p>

                {/* Referral Code Share Row */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  <label style={fieldLabelStyle(isDark)}>
                    <span>Your Referral Code</span>
                  </label>

                  <div style={{ display: 'flex', gap: '8px' }}>
                    <input
                      type="text"
                      readOnly
                      value={myReferralCode}
                      style={{
                        ...fieldInputStyle(isDark),
                        fontFamily: 'monospace',
                        fontWeight: 700,
                        fontSize: '13px',
                        letterSpacing: '1px',
                        textTransform: 'uppercase',
                        color: '#FCA311',
                        flex: 1
                      }}
                    />
                    <button
                      onClick={handleCopyCode}
                      style={{
                        padding: '0 16px',
                        borderRadius: '8px',
                        border: 'none',
                        background: copied ? '#10B981' : 'var(--brand)',
                        color: '#FFFFFF',
                        fontWeight: 700,
                        fontSize: '12.5px',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px',
                        transition: 'all 0.2s ease',
                        outline: 'none',
                        minWidth: '100px',
                        justifyContent: 'center'
                      }}
                    >
                      {copied ? <Check size={14} /> : <Copy size={14} />}
                      <span>{copied ? 'Copied!' : 'Copy Code'}</span>
                    </button>
                  </div>
                </div>

                {/* Claim Referral Code Row */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginTop: '4px' }}>
                  <label style={fieldLabelStyle(isDark)}>
                    <span>Redeem Friend's Code (+5 Credits)</span>
                  </label>

                  {referredByCode ? (
                    <div style={{
                      padding: '10px 14px',
                      borderRadius: '8px',
                      background: isDark ? 'rgba(16, 185, 129, 0.1)' : 'rgba(16, 185, 129, 0.05)',
                      border: `1px solid ${isDark ? 'rgba(16, 185, 129, 0.2)' : 'rgba(16, 185, 129, 0.1)'}`,
                      color: '#10B981',
                      fontSize: '12px',
                      fontWeight: 600,
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px'
                    }}>
                      <Check size={14} />
                      <span>Referral code applied: <strong>{referredByCode}</strong></span>
                    </div>
                  ) : (
                    <form onSubmit={handleApplyReferral} style={{ display: 'flex', gap: '8px' }}>
                      <input
                        type="text"
                        placeholder="e.g. alex-smith"
                        value={claimInput}
                        onChange={e => setClaimInput(e.target.value)}
                        disabled={claimLoading}
                        style={{
                          ...fieldInputStyle(isDark),
                          cursor: 'text',
                          flex: 1
                        }}
                      />
                      <button
                        type="submit"
                        disabled={claimLoading || !claimInput.trim()}
                        style={{
                          padding: '0 16px',
                          borderRadius: '8px',
                          border: 'none',
                          background: (claimLoading || !claimInput.trim()) ? (isDark ? '#27272a' : '#E5E7EB') : 'var(--brand)',
                          color: (claimLoading || !claimInput.trim()) ? (isDark ? '#71717a' : '#9CA3AF') : '#FFFFFF',
                          fontWeight: 700,
                          fontSize: '12.5px',
                          cursor: (claimLoading || !claimInput.trim()) ? 'not-allowed' : 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          transition: 'all 0.2s ease',
                          outline: 'none',
                          minWidth: '100px'
                        }}
                      >
                        {claimLoading ? 'Applying...' : 'Apply Code'}
                      </button>
                    </form>
                  )}

                  {claimError && (
                    <div style={{ color: '#EF4444', fontSize: '11px', fontWeight: 600, marginTop: '2px' }}>
                      ⚠️ {claimError}
                    </div>
                  )}
                  {claimSuccess && (
                    <div style={{ color: '#10B981', fontSize: '11px', fontWeight: 600, marginTop: '2px' }}>
                      🎉 {claimSuccess}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

      </div>
    </div>
  </div>
  );
}

const fieldLabelStyle = (isDark) => ({
  fontSize: '11px',
  fontWeight: 700,
  color: isDark ? '#9ca3af' : '#4b5563',
  textTransform: 'uppercase',
  letterSpacing: '0.5px',
  display: 'flex',
  alignItems: 'center',
  gap: '4px'
});

const fieldInputStyle = (isDark) => ({
  width: '100%',
  background: isDark ? '#1F1F24' : '#F9FAFB',
  border: `1px solid ${isDark ? '#27272a' : '#E5E7EB'}`,
  borderRadius: '8px',
  color: isDark ? '#f4f4f5' : '#0f172a',
  fontSize: '13px',
  padding: '10px 14px',
  outline: 'none',
  boxSizing: 'border-box',
  cursor: 'default'
});
